import { useEffect, useState } from 'react';
import { useSimulationStore } from '@/store/simulationStore';
import type { Alarm, LogEvent } from '@/simulation/alarms/AlarmManager';
import { Button } from '@/ui/components/Button';
import { useUiStore } from '@/store/uiStore';
import styles from './AlarmPanel.module.css';

export function AlarmPanel() {
  const engine = useSimulationStore((state) => state.engine);
  // Re-render when snapshot updates to get fresh alarms
  const snapshot = useSimulationStore((state) => state.snapshot);
  
  const [filter, setFilter] = useState<'ACTIVE' | 'HISTORY' | 'EVENTS'>('ACTIVE');
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [events, setEvents] = useState<LogEvent[]>([]);

  useEffect(() => {
    if (filter === 'ACTIVE') {
      setAlarms(engine.alarms.getActiveAlarms());
    } else if (filter === 'HISTORY') {
      setAlarms(engine.alarms.getAlarms());
    } else {
      setEvents(engine.alarms.getEvents());
    }
  }, [snapshot, filter, engine]);

  const handleAck = (id: string) => {
    engine.alarms.acknowledgeAlarm(id, engine.simulationTimeMs);
    // Force a snapshot update to reflect change immediately
    useSimulationStore.getState().updateSnapshot();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'var(--color-error)';
      case 'ERROR': return 'var(--color-error)';
      case 'WARNING': return 'var(--color-warning)';
      case 'INFO': return 'var(--color-success)';
      default: return 'var(--color-text-dim)';
    }
  };

  const formatTime = (simTimeMs: number) => {
    const s = Math.floor(simTimeMs / 1000);
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    return `${h.toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
  };

  const handleSelectDevice = (deviceId: string) => {
    const device = engine.process.getDevice(deviceId);
    if (device) {
      useUiStore.getState().select(deviceId, device.type);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Button
            variant={filter === 'ACTIVE' ? 'primary' : 'secondary'}
            onClick={() => setFilter('ACTIVE')}
          >
            Active Alarms
          </Button>
          <Button
            variant={filter === 'HISTORY' ? 'primary' : 'secondary'}
            onClick={() => setFilter('HISTORY')}
          >
            Alarm History
          </Button>
          <Button
            variant={filter === 'EVENTS' ? 'primary' : 'secondary'}
            onClick={() => setFilter('EVENTS')}
          >
            Event Log
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        {filter !== 'EVENTS' && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Severity</th>
                <th>State</th>
                <th>Source</th>
                <th>Code</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {alarms.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.empty}>No alarms</td>
                </tr>
              ) : (
                alarms.map((alarm) => (
                  <tr key={alarm.id} className={`${styles.row} ${alarm.state === 'NORMAL' ? styles.normalRow : ''}`}>
                    <td>{formatTime(alarm.firstOccurrenceTime)}</td>
                    <td style={{ color: getSeverityColor(alarm.severity), fontWeight: 'bold' }}>{alarm.severity}</td>
                    <td>
                      {alarm.state === 'ACTIVE' && <span className={styles.badgeActive}>ACTIVE</span>}
                      {alarm.state === 'NORMAL' && <span className={styles.badgeNormal}>NORMAL</span>}
                    </td>
                    <td>
                      <span className={styles.link} onClick={() => handleSelectDevice(alarm.sourceId)}>
                        {alarm.sourceId}
                      </span>
                    </td>
                    <td>{alarm.code}</td>
                    <td>{alarm.title}: {alarm.description}</td>
                    <td>
                      {!alarm.acknowledged && (
                        <Button variant="secondary" onClick={() => handleAck(alarm.id)}>
                          ACK
                        </Button>
                      )}
                      {alarm.acknowledged && (
                        <span className={styles.acked}>Ack'd at {formatTime(alarm.ackTime ?? 0)}</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {filter === 'EVENTS' && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Source</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.empty}>No events</td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id} className={styles.row}>
                    <td>{formatTime(event.simTime)}</td>
                    <td>{event.type}</td>
                    <td>
                      <span className={styles.link} onClick={() => handleSelectDevice(event.source)}>
                        {event.source}
                      </span>
                    </td>
                    <td>{event.description}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
